"use client";

import type { AiMarkerKind } from "@repo/ai-disclaimer";
import { resolveAiMarker } from "@repo/utils/aiMarkerRegistry";
import { cn } from "@repo/utils/cn";
import {
  resolveAiIcon,
  type AiIconVariant,
} from "@repo/utils/aiDisclaimerIcons";
import {
  useAiBadgePosition,
  useAiBadgeSize,
  useAiDisclaimerIcons,
  useAiMarkerRegistry,
} from "./AiDisclaimerProvider";
import { useAiBadgeVariant } from "./useAiBadgeVariant";
import {
  badgeBox,
  badgeInset,
  compactBadgeBox,
  compactBadgeSize,
} from "@repo/utils/aiIconVariant";
import type { AiBadgePosition } from "@repo/utils/aiBadgePosition";
import type { AiBadgeSize } from "@repo/utils/aiBadgeSize";
import { useState } from "react";

/**
 * What a screen reader announces for each marker kind.
 *
 * Deliberately NOT the `DISCLAIMERS` copy from the core: that text is still prefixed
 * `PENDING_LEGAL_` and reading unapproved wording aloud is the exact leak the prefix
 * exists to prevent. These strings are neutral descriptions, replaced by the bound
 * asset's own alt text once real icons land.
 */
const ACCESSIBLE_NAME: Record<AiMarkerKind, string> = {
  generated: "AI-generated image",
  modified: "AI-edited image",
  unspecified: "Image created with AI",
};

/**
 * Where the badge is pinned, per corner and per size.
 *
 * Spelled out as whole class strings rather than composed from an edge and an inset:
 * Tailwind scans source text, so a class assembled at runtime is a class that is never
 * generated — and the badge would end up pinned to the top-left of its container by the
 * bare `absolute`, over whatever happens to be there.
 */
const POSITION_CLASS: Record<
  AiBadgePosition,
  { 12: string; 28: string; compact: string }
> = {
  "top-left": {
    12: "ui:top-3 ui:left-3",
    28: "ui:top-7 ui:left-7",
    compact: "ui:top-1 ui:left-1",
  },
  "top-right": {
    12: "ui:top-3 ui:right-3",
    28: "ui:top-7 ui:right-7",
    compact: "ui:top-1 ui:right-1",
  },
  "bottom-left": {
    12: "ui:bottom-3 ui:left-3",
    28: "ui:bottom-7 ui:left-7",
    compact: "ui:bottom-1 ui:left-1",
  },
  "bottom-right": {
    12: "ui:bottom-3 ui:right-3",
    28: "ui:bottom-7 ui:right-7",
    compact: "ui:bottom-1 ui:right-1",
  },
};

/** The width clamp for each inset: the container less the inset on both sides. */
const CLAMP_CLASS: Record<12 | 28, string> = {
  12: "ui:max-w-[calc(100%-1.5rem)]",
  28: "ui:max-w-[calc(100%-3.5rem)]",
};

/**
 * The artwork height for each site-wide size, as whole class strings for the same reason as
 * `POSITION_CLASS`: a class assembled at runtime is never generated.
 */
const HEIGHT_CLASS: Record<AiBadgeSize, string> = {
  48: "ui:h-12",
  32: "ui:h-8",
  28: "ui:h-7",
  24: "ui:h-6",
  20: "ui:h-5",
  16: "ui:h-4",
};

export type AiDisclaimerProps = {
  kind: AiMarkerKind;
  /**
   * Which artwork to use — `light` is the badge for a light image, so its pill is dark.
   *
   * Omit it and pass `imageSrc` instead to have the background measured.
   */
  variant?: AiIconVariant;
  /**
   * Opt out of measuring the background and use `variant` (or the default) as given.
   *
   * The image itself is found from the DOM, so no src has to be threaded here: only the
   * browser knows the painted size, the crop and the candidate `srcset` actually chose.
   */
  autoVariant?: boolean;
  /**
   * `compact` for thumbnails, where the default badge would cover much of the image.
   * `ArticleList` renders its media as a 107px thumbnail.
   *
   * `default` renders at the site-wide size editors pick on the labelling page; `compact`
   * follows it via `compactBadgeSize` — 32px under the 48px default, 16px under the smaller
   * sizes — so a thumbnail badge is never larger than the one on a full-size image.
   */
  size?: "default" | "compact";
  /**
   * Which corner to pin the badge to.
   *
   * Omitted, it follows the asset's own override and then the site-wide default, so a
   * call site only passes this when its layout leaves the badge nowhere else to go.
   */
  position?: AiBadgePosition;
  /** Escape hatch for a per-image icon, bypassing the provider. */
  iconSrc?: string;
  className?: string;
};

/**
 * The AI-labeling badge, positioned over an image.
 *
 * Renders a plain `<img>` rather than `next/image` on purpose. This repo configures a
 * global `loaderFile` (`storyblokLoader`), so every `next/image` src is rewritten into a
 * Storyblok transform URL — which would corrupt a badge icon served from anywhere else,
 * including the data-URI placeholders. The badge is a fixed-size decorative overlay, so
 * it gains nothing from the loader anyway.
 *
 * Two layout properties are load-bearing rather than stylistic:
 *
 * - `pointer-events-none` — several swept components put a full-bleed clickable layer
 *   over the whole image (`CardParking` has a `z-20` link covering the card). A badge
 *   that captured clicks would punch a dead spot in that card's only link.
 * - `isolate` — sibling overlays use `mix-blend-mode` (see `ImageOverlay`), which would
 *   otherwise blend the badge into the image and can destroy its contrast.
 *
 * `z-30` clears the layers actually present on the swept components: decorative
 * gradients at `z-[5]`, carousel content at `z-10`, and that `z-20` card link. Sitting
 * above the link is safe precisely because the badge ignores pointer events.
 */
export function AiDisclaimer({
  kind,
  variant,
  autoVariant = true,
  size = "default",
  position,
  iconSrc,
  className,
}: AiDisclaimerProps) {
  const configuredIcons = useAiDisclaimerIcons();
  const defaultPosition = useAiBadgePosition();
  const resolvedPosition = position ?? defaultPosition;
  const [node, setNode] = useState<HTMLElement | null>(null);
  const siteSize = useAiBadgeSize();
  const isCompact = size === "compact";
  const inset = badgeInset(siteSize);
  // The measurement must cover what the badge covers, so the box follows the size the
  // component renders at rather than the default.
  const footprint = isCompact
    ? compactBadgeBox(siteSize)
    : badgeBox(siteSize, inset);
  // An explicit `variant` always wins, and no measurement is requested for it.
  const measured = useAiBadgeVariant(
    node,
    autoVariant && !variant,
    footprint.width,
    footprint.height,
    // The corner the badge is pinned to is the corner whose contrast decides its
    // artwork. Measuring anywhere else answers about pixels the badge does not cover.
    resolvedPosition,
  );
  // `light` until measured, rather than nothing: the badge is the compliance artifact, so
  // it renders immediately and improves its contrast when the answer arrives.
  const resolvedVariant = variant ?? measured ?? "light";
  const src = iconSrc || resolveAiIcon(kind, resolvedVariant, configuredIcons);

  return (
    <span
      ref={setNode}
      data-ai-disclaimer={kind}
      data-ai-badge-position={resolvedPosition}
      data-ai-badge-size={isCompact ? "compact" : siteSize}
      className={cn(
        "ui:pointer-events-none ui:absolute ui:isolate ui:z-30",
        // Never wider than the space left beside its own inset.
        //
        // The artwork is ~51px wide at 16px and ~152px at 48px, and several call sites paint it over containers
        // narrower than that — a card thumbnail, a narrow media column. Without the clamp
        // the badge overflows and the container's `overflow-hidden` slices it, which is a
        // compliance artifact that is present in the DOM and unreadable on screen. The
        // corner only decides which edge does the slicing.
        isCompact ? "ui:max-w-[calc(100%-0.5rem)]" : CLAMP_CLASS[inset],
        POSITION_CLASS[resolvedPosition][isCompact ? "compact" : inset],
        className,
      )}
    >
      <img
        src={src}
        alt={ACCESSIBLE_NAME[kind]}
        // Marks the badge's own image so the measurement does not mistake it for the
        // picture it labels.
        data-ai-badge=""
        // `object-contain` is what makes the clamp scale the artwork rather than squash
        // it. A replaced element keeps its ratio through a max-width only while BOTH
        // axes are auto; here the height is fixed, so the box narrows and the image
        // would stretch. `contain` fits the artwork inside that narrowed box instead —
        // visible on the 107px `ArticleList` thumbnails.
        className={cn(
          "ui:block ui:w-auto ui:max-w-full ui:object-contain",
          HEIGHT_CLASS[isCompact ? compactBadgeSize(siteSize) : siteSize],
        )}
      />
    </span>
  );
}

export type AiDisclaimerForAltProps = Omit<AiDisclaimerProps, "kind"> & {
  /** The raw, still-marked alt text straight from the CMS. */
  alt: string | null | undefined;
  /**
   * The image this badge labels, so a marked asset is recognised even when the alt does
   * not say so.
   *
   * Storyblok copies an asset's alt into a story field when the asset is picked and then
   * stops linking the two, so every placement made before the asset was marked carries an
   * alt with no marker in it. Passing the url lets the registry answer for those.
   *
   * Optional: without it the badge behaves exactly as it did before the registry existed.
   *
   * A video url is resolved the same way. A video asset carries no alt at its call
   * sites, so the registry is the only thing that can answer for one — which is why this
   * is the parameter that matters for them, not `alt`.
   */
  src?: string | null;
};

/**
 * Renders the badge for a raw alt string, or nothing when the alt carries no marker.
 *
 * This exists because the badge is added at many call sites that each render their own
 * `<Image>`, most of them inside a `{image && ...}` conditional. A hook could not be
 * called from inside those conditionals, and repeating the marker destructure at every
 * site invites exactly the copy-paste slip this sweep cannot afford — one missed `isAi`
 * check and an unmarked image gets a badge, or a marked one silently loses it.
 *
 * Concentrating it here is what keeps the registry lookup to one place too: the callers
 * pass a url and an alt, and never decide anything themselves.
 *
 * The alt is parsed twice per site: once by the caller for `cleanAlt`, once here. That is
 * a regex over a short string against a correctness win at every site.
 */
export function AiDisclaimerForAlt({
  alt,
  src,
  position,
  ...props
}: AiDisclaimerForAltProps) {
  // The single place the registry is consulted. Every badge in this package renders
  // through here, so the alt-or-registry decision is made once rather than at each site —
  // the same reason this wrapper exists at all.
  const registry = useAiMarkerRegistry();
  const {
    isAi,
    kind,
    position: assetPosition,
  } = resolveAiMarker(src, alt, registry);

  if (!isAi || !kind) {
    return null;
  }

  // A call site that names a corner outranks the asset's own: it is a statement about
  // this layout, where the asset's override is a statement about the artwork. Neither
  // being set falls through to the site-wide default inside `AiDisclaimer`.
  return (
    <AiDisclaimer
      kind={kind}
      position={position ?? assetPosition ?? undefined}
      {...props}
    />
  );
}
